import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Badge,
  Button,
  Card,
  Spinner,
  useToast,
} from '../components/ui';
import { Mail } from 'lucide-react';
import { VendorOverview } from '../components/vendors/VendorOverview';
import { ObservationsGrid } from '../components/vendors/ObservationsGrid';
import { tokenManager } from '../lib/api';
import { useViewportContext } from '../hooks/useViewportContext';
import { useAgent } from '../contexts/AgentContext';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

interface Vendor {
  id: number;
  name: string;
  website: string;
  description?: string;
  headquarters_location?: string;
  employee_count?: number;
  primary_contact_name?: string;
  primary_contact_email?: string;
  primary_contact_phone?: string;
}

interface Product {
  id: number;
  name: string;
  product_type: string;
  description?: string;
}

interface Observation {
  id: number;
  entity_type: string;
  spec: number;
  spec_name: string;
  spec_unit?: string;
  value_numeric?: number;
  value_text?: string;
  value_boolean?: boolean;
  source_type: string;
  confidence: number;
  observed_date: string;
  notes?: string;
}

interface ScorecardSummary {
  score: number;
  completeness: number;
  evaluation_id?: string;
}

interface VendorEvaluationSummary {
  id: string;
  vendor: any;
  product: Product | null;
  overall_score: number;
  meets_threshold: boolean;
  strengths: string[];
  gaps: string[];
  evaluated_at: string;
  scorecard_summary: {
    technical?: ScorecardSummary;
    manufacturing_readiness?: ScorecardSummary;
    vc_metrics?: ScorecardSummary;
    dod_partnership?: ScorecardSummary;
  };
}

interface ScorecardEvaluationDetail {
  id: string;
  overall_score: number;
  completeness_percentage: number;
  executive_summary?: string;
  evaluated_at: string;
  section_evaluations?: any[];
}

const VendorDetail = () => {
  const { programId, vendorId } = useParams<{ programId: string; vendorId: string }>();
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const { addToast } = useToast();
  
  // New state for program-specific data
  const [evaluationSummary] = useState<VendorEvaluationSummary | null>(null);
  const [selectedProductId] = useState<number | null>(null);
  const [productObservations, setProductObservations] = useState<Observation[]>([]);
  const [vendorObservations, setVendorObservations] = useState<Observation[]>([]);
  const [detailedEvaluations, _setDetailedEvaluations] = useState<{
    technical?: ScorecardEvaluationDetail;
    manufacturing?: ScorecardEvaluationDetail;
    vc?: ScorecardEvaluationDetail;
    dod?: ScorecardEvaluationDetail;
  }>({});

  const { updatePageContext } = useAgent();

  // Tier 1: Enrich metadata with vendor name
  useEffect(() => {
    if (vendor) {
      updatePageContext({ vendorName: vendor.name });
    }
  }, [vendor, updatePageContext]);

  // Tier 2: Register viewport serializer
  useViewportContext('vendor_detail', () => ({
    vendor: vendor ? {
      id: vendor.id,
      name: vendor.name,
      website: vendor.website,
      description: vendor.description,
      headquarters: vendor.headquarters_location,
      employee_count: vendor.employee_count,
    } : null,
    evaluation_summary: evaluationSummary ? {
      overall_score: evaluationSummary.overall_score,
      meets_threshold: evaluationSummary.meets_threshold,
      strengths: evaluationSummary.strengths,
      gaps: evaluationSummary.gaps,
      product: evaluationSummary.product ? {
        id: evaluationSummary.product.id,
        name: evaluationSummary.product.name,
      } : null,
      scorecard_summary: evaluationSummary.scorecard_summary,
    } : null,
    detailed_evaluations: {
      technical: detailedEvaluations.technical ? {
        overall_score: detailedEvaluations.technical.overall_score,
        completeness: detailedEvaluations.technical.completeness_percentage,
        summary: detailedEvaluations.technical.executive_summary,
      } : null,
      manufacturing: detailedEvaluations.manufacturing ? {
        overall_score: detailedEvaluations.manufacturing.overall_score,
        completeness: detailedEvaluations.manufacturing.completeness_percentage,
        summary: detailedEvaluations.manufacturing.executive_summary,
      } : null,
      vc: detailedEvaluations.vc ? {
        overall_score: detailedEvaluations.vc.overall_score,
        completeness: detailedEvaluations.vc.completeness_percentage,
        summary: detailedEvaluations.vc.executive_summary,
      } : null,
      dod: detailedEvaluations.dod ? {
        overall_score: detailedEvaluations.dod.overall_score,
        completeness: detailedEvaluations.dod.completeness_percentage,
        summary: detailedEvaluations.dod.executive_summary,
      } : null,
    },
  }), [vendor, evaluationSummary, detailedEvaluations]);

  // Fetch vendor and related data
  useEffect(() => {
    const fetchData = async () => {
      if (!vendorId || !programId) return;

      try {
        setLoading(true);
        setError(null);
        
        const token = tokenManager.getAccessToken();
        if (!token) {
          setError('Authentication required');
          return;
        }
        
        // Fetch vendor data
        const vendorResponse = await fetch(`${API_BASE_URL}/api/vendors/${vendorId}/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
        
        if (!vendorResponse.ok) {
          throw new Error('Failed to fetch vendor data');
        }
        
        const vendorData = await vendorResponse.json();
        setVendor(vendorData);

        // Fetch vendor observations
        const vendorObsResponse = await fetch(
          `${API_BASE_URL}/api/observations/?vendor=${vendorId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
          }
        );
        if (vendorObsResponse.ok) {
          const obsData = await vendorObsResponse.json();
          // Handle both array and paginated response formats
          setVendorObservations(Array.isArray(obsData) ? obsData : (obsData.results || []));
        }

      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load vendor details');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [vendorId, programId]);
  
  // Fetch product observations when product is selected
  useEffect(() => {
    const fetchProductObservations = async () => {
      if (!selectedProductId) return;
      
      try {
        const token = tokenManager.getAccessToken();
        if (!token) return;
        
        const response = await fetch(
          `${API_BASE_URL}/api/observations/?product=${selectedProductId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
          }
        );
        if (response.ok) {
          const data = await response.json();
          // Handle both array and paginated response formats
          setProductObservations(Array.isArray(data) ? data : (data.results || []));
        }
      } catch (err) {
        console.error('Error fetching product observations:', err);
        setProductObservations([]); // Ensure it's always an array
      }
    };
    
    fetchProductObservations();
  }, [selectedProductId]);

  // Handle invite to apply
  const handleInviteToApply = async () => {
    if (!vendorId || !programId) return;
    
    setInviting(true);
    try {
      const token = tokenManager.getAccessToken();
      if (!token) {
        addToast({
          variant: 'error',
          title: 'Authentication required',
          description: 'Please log in to send invitations'
        });
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/vendors/${vendorId}/invite-to-apply/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          program_id: programId
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send invitation');
      }
      
      await response.json();
      addToast({
        variant: 'success',
        title: 'Invitation sent!',
        description: `Email sent to ${vendor?.primary_contact_email || 'vendor'}`
      });
      
    } catch (err) {
      console.error('Error sending invitation:', err);
      addToast({
        variant: 'error',
        title: 'Failed to send invitation',
        description: err instanceof Error ? err.message : 'An error occurred'
      });
    } finally {
      setInviting(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-background">
        <div className="text-center">
          <Spinner size="lg" className="mx-auto mb-4" />
          <p className="text-muted-foreground">Loading vendor details...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error || !vendor) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-background">
        <Card className="p-6 max-w-md">
          <h2 className="text-xl font-semibold text-destructive mb-2">
            Error Loading Vendor
          </h2>
          <p className="text-muted-foreground">
            {error || 'Vendor not found'}
          </p>
        </Card>
      </div>
    );
  }

  // Extract vendor display data
  const vendorDisplay = {
    name: vendor.name,
    status: 'active' as const,
    contactDate: 'Never contacted', // TODO: Add contact tracking
    submittedAt: 'N/A',
    primaryContact: vendor.primary_contact_name || 'N/A',
    primaryEmail: vendor.primary_contact_email || 'N/A',
    summary: vendor.description || 'No description available.',
  };

  return (
    <div className="h-full w-full overflow-auto px-8 py-8 bg-background">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h1 className="text-4xl font-bold text-foreground mb-3">
              {vendorDisplay.name}
            </h1>
            <div className="flex items-center gap-3">
              <Badge variant="pending">
                {vendorDisplay.status}
              </Badge>
              {vendor.headquarters_location && (
                <span className="text-muted-foreground text-sm">
                  {vendor.headquarters_location}
                </span>
              )}
            </div>
          </div>
          <div>
            <Button
              onClick={handleInviteToApply}
              disabled={inviting || !vendor.primary_contact_email}
              className="flex items-center gap-2"
              title={!vendor.primary_contact_email ? 'Vendor has no contact email' : 'Send invitation to apply'}
            >
              <Mail className="h-4 w-4" />
              {inviting ? 'Sending...' : 'Invite to Apply'}
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-white dark:bg-slate-900 border-b border-border rounded-none w-full justify-start h-auto p-0 mb-6">
          <TabsTrigger
            value="overview"
            className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none text-muted-foreground data-[state=active]:text-slate-900 dark:data-[state=active]:text-slate-50 pb-3 pt-3 px-4"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="technical"
            className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none text-muted-foreground data-[state=active]:text-slate-900 dark:data-[state=active]:text-slate-50 pb-3 pt-3 px-4"
          >
            Technical Fit
          </TabsTrigger>
          <TabsTrigger
            value="manufacturing"
            className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none text-muted-foreground data-[state=active]:text-slate-900 dark:data-[state=active]:text-slate-50 pb-3 pt-3 px-4"
          >
            Production Readiness
          </TabsTrigger>
          <TabsTrigger
            value="company"
            className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none text-muted-foreground data-[state=active]:text-slate-900 dark:data-[state=active]:text-slate-50 pb-3 pt-3 px-4"
          >
            VC Scorecard
          </TabsTrigger>
          <TabsTrigger
            value="compliance"
            className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none text-muted-foreground data-[state=active]:text-slate-900 dark:data-[state=active]:text-slate-50 pb-3 pt-3 px-4"
          >
            DoW Partnership
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab - Vendor View */}
        <TabsContent value="overview" className="mt-0">
          <VendorOverview 
            vendor={vendorDisplay}
            showReviewerAssignment={true}
            showSubmittedDocuments={false}
            programContext={true}
          />
        </TabsContent>

        {/* Technical Fit Tab */}
        <TabsContent value="technical" className="mt-0">
          <div className="space-y-6">
            {/* Product Info and Technical Score */}
            {evaluationSummary?.product && evaluationSummary.scorecard_summary?.technical ? (
              <>
                {/* Product Details */}
                <Card className="p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    Product: {evaluationSummary.product.name}
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <span className="text-xs font-medium text-muted-foreground uppercase">Type</span>
                      <p className="text-sm text-foreground mt-1">{evaluationSummary.product.product_type}</p>
                    </div>
                    {evaluationSummary.product.description && (
                      <div>
                        <span className="text-xs font-medium text-muted-foreground uppercase">Description</span>
                        <p className="text-sm text-card-foreground mt-1">{evaluationSummary.product.description}</p>
                      </div>
                    )}
                  </div>
                </Card>
                
                {/* Technical Score */}
                <Card className="p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    Overall Technical Fit Score
                  </h3>
                  <div className="flex items-start gap-4">
                    <div className="text-center">
                      <div className="text-5xl font-bold text-primary">
                        {Math.round(evaluationSummary.scorecard_summary.technical.score)}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        out of 100
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-card-foreground">
                        Technical fit analysis based on program requirements and product capabilities.
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Data completeness: {Math.round(evaluationSummary.scorecard_summary.technical.completeness)}%
                      </p>
                    </div>
                  </div>
                </Card>
                
                {/* Technical Observations */}
                <ObservationsGrid 
                  observations={(Array.isArray(productObservations) ? productObservations : []).filter(o => 
                    o.spec_name && (
                      o.spec_name.toLowerCase().includes('range') ||
                      o.spec_name.toLowerCase().includes('payload') ||
                      o.spec_name.toLowerCase().includes('speed') ||
                      o.spec_name.toLowerCase().includes('endurance') ||
                      o.spec_name.toLowerCase().includes('altitude')
                    )
                  )}
                  title="Technical Specifications (Observed Data)"
                />
              </>
            ) : (
              <Card className="p-6">
                <p className="text-slate-500 dark:text-muted-foreground italic text-center">
                  Technical evaluation data not available for this vendor.
                </p>
              </Card>
            )}

          </div>
        </TabsContent>

        {/* Production Readiness Tab */}
        <TabsContent value="manufacturing" className="mt-0">
          <div className="space-y-6">
            {evaluationSummary?.scorecard_summary?.manufacturing_readiness ? (
              <>
                {/* Overall Rating */}
                <Card className="p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    Overall Production Readiness Score
                  </h3>
                  <div className="flex items-start gap-4">
                    <div className="text-center">
                      <div className="text-5xl font-bold text-orange-600 dark:text-orange-400">
                        {Math.round(evaluationSummary.scorecard_summary.manufacturing_readiness.score)}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        out of 100
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-card-foreground">
                        Production readiness assessment based on TRL, testing level, reliability, and availability.
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Data completeness: {Math.round(evaluationSummary.scorecard_summary.manufacturing_readiness.completeness)}%
                      </p>
                    </div>
                  </div>
                </Card>

                {/* Production Readiness Observations */}
                <ObservationsGrid
                  observations={(Array.isArray(vendorObservations) ? vendorObservations : []).filter(o =>
                    o.spec_name && (o.spec_name.toLowerCase().includes('trl') || o.spec_name.toLowerCase().includes('testing') || o.spec_name.toLowerCase().includes('reliab') || o.spec_name.toLowerCase().includes('available'))
                  )}
                  title="Production Readiness Data (Observed)"
                />
                
                {/* Strengths and Gaps */}
                {evaluationSummary.strengths && evaluationSummary.strengths.length > 0 && (
                  <Card className="p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-4">
                      Key Strengths
                    </h3>
                    <div className="space-y-2">
                      {evaluationSummary.strengths.slice(0, 5).map((strength: string, idx: number) => (
                        <div key={idx} className="flex items-start gap-2 text-sm text-card-foreground">
                          <span className="text-success">✓</span>
                          <span>{strength}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </>
            ) : (
              <Card className="p-6">
                <p className="text-slate-500 dark:text-muted-foreground italic text-center">
                  Production readiness data not yet available for this vendor.
                </p>
              </Card>
            )}

          </div>
        </TabsContent>

        {/* VC Scorecard Tab */}
        <TabsContent value="company" className="mt-0">
          <div className="space-y-6">
            {evaluationSummary?.scorecard_summary?.vc_metrics ? (
              <>
                {/* Overall Rating */}
                <Card className="p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    Overall Company Stability Score
                  </h3>
                  <div className="flex items-start gap-4">
                    <div className="text-center">
                      <div className="text-5xl font-bold text-success">
                        {Math.round(evaluationSummary.scorecard_summary.vc_metrics.score)}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        out of 100
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-card-foreground">
                        Company stability assessment based on funding, team size, and customer base.
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Data completeness: {Math.round(evaluationSummary.scorecard_summary.vc_metrics.completeness)}%
                      </p>
                    </div>
                  </div>
                </Card>
                
                {/* VC Observations */}
                <ObservationsGrid 
                  observations={(Array.isArray(vendorObservations) ? vendorObservations : []).filter(o => 
                    o.spec_name && (
                      o.spec_name.toLowerCase().includes('fund') ||
                      o.spec_name.toLowerCase().includes('employee') ||
                      o.spec_name.toLowerCase().includes('revenue')
                    )
                  )}
                  title="Company Stability Data (Observed)"
                />
              </>
            ) : (
              <Card className="p-6">
                <p className="text-slate-500 dark:text-muted-foreground italic text-center">
                  Company stability data not yet available for this vendor.
                </p>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* DoW Partnership Tab */}
        <TabsContent value="compliance" className="mt-0">
          <div className="space-y-6">
            {evaluationSummary?.scorecard_summary?.dod_partnership ? (
              <>
                {/* Overall Rating */}
                <Card className="p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    Overall DoW Partnership Score
                  </h3>
                  <div className="flex items-start gap-4">
                    <div className="text-center">
                      <div className="text-5xl font-bold text-indigo-600 dark:text-indigo-400">
                        {Math.round(evaluationSummary.scorecard_summary.dod_partnership.score)}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        out of 100
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-card-foreground">
                        DoW partnership readiness based on registration, compliance, contract history, and risk factors.
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Data completeness: {Math.round(evaluationSummary.scorecard_summary.dod_partnership.completeness)}%
                      </p>
                    </div>
                  </div>
                </Card>
                
                {/* Compliance Observations */}
                <ObservationsGrid 
                  observations={(Array.isArray(vendorObservations) ? vendorObservations : []).filter(o => 
                    o.spec_name && (
                      o.spec_name.toLowerCase().includes('cmmc') ||
                      o.spec_name.toLowerCase().includes('clearance') ||
                      o.spec_name.toLowerCase().includes('certification')
                    )
                  )}
                  title="Compliance Data (Observed)"
                />
                
                {/* Gaps from pathway evaluation */}
                {evaluationSummary.gaps && evaluationSummary.gaps.length > 0 && (
                  <Card className="p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-4">
                      Areas for Improvement
                    </h3>
                    <div className="space-y-2">
                      {evaluationSummary.gaps.slice(0, 5).map((gap: string, idx: number) => (
                        <div key={idx} className="flex items-start gap-2 text-sm text-card-foreground">
                          <span className="text-orange-600 dark:text-orange-400">!</span>
                          <span>{gap}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </>
            ) : (
              <Card className="p-6">
                <p className="text-slate-500 dark:text-muted-foreground italic text-center">
                  DoW partnership readiness data not yet available for this vendor.
                </p>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default VendorDetail;

